import { useCallback, useEffect } from 'react';

import { GridReadyEvent } from 'ag-grid-community';
import { Dialog } from 'primereact/dialog';
import { useSearchParams } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';

import { FreezeActions } from './components/FreezeActions';
import { FreezeProvider } from './components/FreezeContext';
import { FreezeEditBar } from './components/FreezeEditBar';
import { FreezeFertilizationGrid } from './components/FreezeFertilizationGrid';
import FreezeGrading from './components/FreezeGrading';
import { FreezeMetrics } from './components/FreezeMetrics';
import { FreezeQC } from './components/FreezeQC';
import { FreezeSelector } from './components/FreezeSelector';
import { FreezeToolbar } from './components/FreezeToolbar';
import { FreezeValidation } from './components/FreezeValidation';
import { useDaySelection } from './hooks/useDaySelection';
import { useFreeze } from './hooks/useFreeze';
import { useFreezeContext } from './hooks/useFreezeContext';
import { useGridOperations } from './hooks/useGridOperations/index';
import { useGridSelection } from './hooks/useGridSelection';
import { useValidation } from './hooks/useValidation';
import { SelectionService } from './utils/selectionService';

import './index.css';

type FreezePageProps = {
  className?: string;
};

const FreezePageContent = ({ className }: FreezePageProps) => {
  const { mode } = useAppSelector((state) => state.preferences);
  const {
    showFreezeToolbar,
    showFreezeQC,
    showFreezeGrading,
    showFreezeValidation,
    showFreezeMetrics,
    showEditView,
    form,
    qcForm,
    setIsUpdateRunning,
    allLiveEmbryosSelected,
    allTempEmbryosSelected,
  } = useFreezeContext();

  const [params] = useSearchParams();
  const fertilizationDate = params.get('date') || new Date().toString();
  const fertilizationSiteId = params.get('sId');

  const {
    gridRef,
    gridApi,
    setGridApi,
    quickSearchValue,
    setQuickSearchValue,
    handleExpandAll,
    onCellKeyDown,
    handleFertilizationSelectionChanged,
    selectFertForLiveEmbryos,
    selectFertForTempEmbryos,
    handleEmbryoGridEdit,
  } = useGridOperations();

  const {
    fertilizationData,
    fertEmbryosData,
    embryosLoading,
    updateODFreezeLoading,
    isUpdatingFertilizations,
    handleUpdateODFreeze,
    handleUpdateFertilization,
    refetchFertEmbryoData,
    dataVersion,
    showIncompleteEmbryosDialog,
    setShowIncompleteEmbryosDialog,
    handleIncompleteEmbryosAccept,
    handleIncompleteEmbryosReject,
    incompleteEmbryosRef,
  } = useFreeze({
    fertilizationDate,
    fertilizationSiteId,
    gridRef,
    searchQuery: quickSearchValue,
  });

  const {
    showMissingLocationsDialog,
    setShowMissingLocationsDialog,
    missingLocationsMessages,
    showNoGraderAndFreezeTech,
    allFertMathCorrect,
    validation,
    checkEmbryoConditions,
    checkEmbryoQCFields,
  } = useValidation({
    fertilizationData,
    fertEmbryosData,
    fertilizationSiteId,
  });

  const { handleCheckDay } = useDaySelection(gridRef, fertEmbryosData);

  useGridSelection({
    gridRef,
    allLiveEmbryosSelected,
    allTempEmbryosSelected,
    fertEmbryosData,
  });

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      setGridApi(params.api);
    },
    [setGridApi],
  );

  useEffect(() => {
    SelectionService.setSelection({
      liveEmbryoIds: [],
      tempEmbryoIds: [],
      fertilizationIds: [],
      lastUpdated: Date.now(),
    });
  }, []);

  return (
    <div className={`common-page freeze ${className || ''}`}>
      <div className="freeze__top-container toolbar-above-panel">
        <div className="freeze__top lg:flex-nowrap lg:items-end">
          <FreezeSelector
            quickSearchValue={quickSearchValue}
            setQuickSearchValue={setQuickSearchValue}
          />
          <FreezeActions
            gridRef={gridRef}
            handleExpandAll={handleExpandAll}
            handleUpdateODFreeze={() =>
              handleUpdateODFreeze(form, qcForm, setIsUpdateRunning)
            }
            updateODFreezeLoading={updateODFreezeLoading}
            embryosLoading={embryosLoading}
            fertilizations={fertilizationData}
            fertEmbryosData={fertEmbryosData}
            handleCheckDay={handleCheckDay}
            showIncompleteEmbryosDialog={showIncompleteEmbryosDialog}
            setShowIncompleteEmbryosDialog={setShowIncompleteEmbryosDialog}
            onIncompleteEmbryosAccept={handleIncompleteEmbryosAccept}
            onIncompleteEmbryosReject={handleIncompleteEmbryosReject}
            incompleteEmbryosRef={incompleteEmbryosRef}
          />
        </div>
        {showFreezeToolbar && <FreezeToolbar />}
        {showEditView && <FreezeEditBar />}
        {showFreezeQC && <FreezeQC />}
        {showFreezeGrading && <FreezeGrading />}

        {showFreezeMetrics && (
          <FreezeMetrics fertilizations={fertilizationData} />
        )}
        {showFreezeValidation && (
          <>
            <FreezeValidation
              checkEmbryoConditions={checkEmbryoConditions}
              checkEmbryoQCFields={checkEmbryoQCFields}
              frozeValidatorId={validation.validatorId as string}
              frozeValidationDate={validation.validationDate as Date}
              missingLocationsMessages={missingLocationsMessages}
              allFertMathCorrect={allFertMathCorrect}
            />
            <Dialog
              visible={showMissingLocationsDialog}
              onHide={() => setShowMissingLocationsDialog(false)}
              header={(() => {
                const issues = [];
                if (missingLocationsMessages.length > 0)
                  issues.push('Missing Storage Locations');
                if (!allFertMathCorrect) issues.push('Validation Failure');
                if (showNoGraderAndFreezeTech)
                  issues.push('Missing Grader or Freeze Tech');
                return issues.length > 0
                  ? issues.join(' & ')
                  : 'Incorrect Math';
              })()}
              closable
              className="missing-locations-dialog"
            >
              <div className="missing-locations-content">
                <ul>
                  {!allFertMathCorrect && (
                    <li>
                      <strong>
                        Validation failure: Embryos unaccounted for — check
                        discards / held to freeze for red matings
                      </strong>
                    </li>
                  )}
                  {missingLocationsMessages?.length > 0 && (
                    <li>
                      <strong>
                        Storage locations{' '}
                        {showNoGraderAndFreezeTech
                          ? 'or grader/freeze tech '
                          : ''}
                        missing from:
                      </strong>
                    </li>
                  )}
                  {missingLocationsMessages?.length > 0 && (
                    <ul>
                      {missingLocationsMessages?.map((msg, index) => (
                        <li key={index}>{msg}</li>
                      ))}
                    </ul>
                  )}
                </ul>
              </div>
            </Dialog>
          </>
        )}
      </div>

      <div className={`freeze__grid`}>
        <FreezeFertilizationGrid
          gridRef={gridRef}
          fertilizationData={fertilizationData}
          fertEmbryosData={fertEmbryosData}
          handleUpdateFertilization={handleUpdateFertilization}
          onGridReady={onGridReady}
          onCellKeyDown={onCellKeyDown}
          handleEmbryoGridEdit={handleEmbryoGridEdit}
          selectFertForLiveEmbryos={selectFertForLiveEmbryos}
          selectFertForTempEmbryos={selectFertForTempEmbryos}
          handleFertilizationSelectionChanged={
            handleFertilizationSelectionChanged
          }
          fertilizationSiteId={fertilizationSiteId}
          isUpdatingFertilizations={isUpdatingFertilizations}
          embryosLoading={embryosLoading}
          mode={mode}
          gridApi={gridApi}
          refetchFertEmbryoData={refetchFertEmbryoData}
          dataVersion={dataVersion}
        />
      </div>
    </div>
  );
};

export const FreezePage = (props: FreezePageProps) => {
  return (
    <FreezeProvider>
      <FreezePageContent {...props} />
    </FreezeProvider>
  );
};
